import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import menu from "../../data/sidebar"
import {
    Bars3Icon,
    XMarkIcon
  } from '@heroicons/react/24/outline'
import SidebarItem from './SidebarItem'
import { useAuth } from '../../context/AuthContext'
import useManageUser from '../../features/user/useManageUser'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux';
import { setShop } from '../../redux/features/shopSlice';

const Sidebar = ({children}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { logout} = useManageUser()
  const { authState, logoutUser } = useAuth();
  const { user } = authState;

  const dispatch = useDispatch();
  const shopName = useSelector((state) => state.shop.name);

  const sidebarColor =
    shopName === 'tama' ? 'bg-yellow-900' : 'bg-indigo-600';

  const handleSwitchShop = (newShop) => {
    dispatch(setShop(newShop)); // Update Redux
    localStorage.setItem('shopName', newShop); // Persist to localStorage
    toast.success(`Switched to ${newShop}`);
    setIsModalOpen(false); // Close modal
    window.location.reload();
  };

  const handleLogout = async () => {
    const response = await logout()
    // Log out locally either way: when the session has already expired the API
    // refuses the logout call itself (401), which used to leave the admin stuck on
    // "Logout Not Success" until a manual F5 (QA 829).
    logoutUser()
    if(response){
      toast.success("Logout Success")
    }
  }

  const filteredMenu = menu.map(item => {
    // Filter the main menu items
    if (!item.roles.includes(user?.role)) {
      return null;
    }
  
    // Filter children if they exist
    const filteredChildren = item.childrens?.filter(child => child.roles.includes(user?.role));
  
    return {
      ...item,
      childrens: filteredChildren,
    };
  }).filter(item => item !== null); // Remove nulls from the main menu

  useEffect(() => {
    console.log(filteredMenu);
    console.log(user);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- log only when filteredMenu changes
  }, [filteredMenu]);

  return (
    <>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5" onClick={() => setSidebarOpen(false)}>
                      <button type="button" className="-m-2.5 p-2.5">
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  {/* Sidebar component, swap this element with another sidebar if you like */}
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-indigo-600 px-6 pb-2">
                    <div className="flex h-16 shrink-0 items-center">
                      <img
                        className="h-8 w-auto"
                        src="https://tailwindui.com/img/logos/mark.svg?color=white"
                        alt="Your Company"
                      />
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        <li>
                          <ul role="list" className="-mx-2 space-y-1">
                            {filteredMenu.map((item, index) => (
                                <SidebarItem key={index} item={item}  />
                            ))}
                          </ul>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-60 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-60 lg:flex-col">
            <div className={`flex grow flex-col gap-y-5 overflow-y-auto ${sidebarColor} px-6 ${sidebarCollapsed ? 'w-20' : 'w-60'}`}>
              <div className="flex h-16 shrink-0 items-center justify-between">
                <img
                  className={`${sidebarCollapsed ? 'h-8' : 'h-16'} w-auto mt-5`}
                  src={
                    shopName === 'tama'
                      ? 'https://res.cloudinary.com/dkeybfbhj/image/upload/v1736100015/resources/qtlcymnurvomr6lexhos.png'
                      : 'https://res.cloudinary.com/dkeybfbhj/image/upload/v1703700428/resources/ej6cuul9mgff3mnmxyfh.png'
                  }
                  alt={shopName === 'tama' ? 'tama Logo' : 'smile Logo'}
                />
                <button
                  className="text-indigo-200 hover:text-white focus:outline-none"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                >
                  {sidebarCollapsed ? (
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {!sidebarCollapsed && (
                <nav className="flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-1">
                        {filteredMenu.map((item, index) => (
                          <SidebarItem key={index} item={item} />
                        ))}
                      </ul>
                    </li>

                    <button
                      className={`mt-4 px-4 py-2 rounded text-white ${
                        shopName === 'tama'
                          ? 'bg-yellow-800 hover:bg-yellow-700'
                          : 'bg-indigo-700 hover:bg-indigo-800'
                      }`}
                      onClick={() => setIsModalOpen(true)}
                    >
                      Switch Shop
                    </button>

                    <Transition.Root show={isModalOpen} as={Fragment}>
                      <Dialog as="div" className="relative z-50" onClose={setIsModalOpen}>
                        <Transition.Child
                          as={Fragment}
                          enter="ease-out duration-300"
                          enterFrom="opacity-0"
                          enterTo="opacity-100"
                          leave="ease-in duration-200"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </Transition.Child>

                        <div className="fixed inset-0 z-10 overflow-y-auto">
                          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                            <Transition.Child
                              as={Fragment}
                              enter="ease-out duration-300"
                              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                              enterTo="opacity-100 translate-y-0 sm:scale-100"
                              leave="ease-in duration-200"
                              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                <div>
                                  <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4 text-center">Switch Shop</h3>
                                  <div className="flex justify-between gap-4">
                                    {/* tama Button */}
                                    <button
                                      className={`flex-1 px-4 py-2 rounded text-white font-semibold bg-yellow-700 hover:bg-yellow-800`}
                                      onClick={() => handleSwitchShop('tama')}
                                    >
                                      tama
                                    </button>

                                    {/* smile Button */}
                                    <button
                                      className={`flex-1 px-4 py-2 rounded text-white font-semibold bg-indigo-700 hover:bg-indigo-800`}
                                      onClick={() => handleSwitchShop('smile')}
                                    >
                                      smile
                                    </button>
                                  </div>
                                </div>
                              </Dialog.Panel>
                            </Transition.Child>
                          </div>
                        </div>
                      </Dialog>
                    </Transition.Root>

                    <li className="-mx-6 mt-auto">
                      <div className="flex justify-between w-full px-6 py-3">
                        <div className="flex-grow"></div>
                        <button
                          className="text-sm font-semibold leading-6 text-white hover:bg-indigo-700 rounded px-4 py-2"
                          onClick={handleLogout}
                        >
                          <span className="text-white">Logout</span>
                        </button>
                      </div>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </div>
        </div>

        <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-indigo-600 px-4 py-4 shadow-sm sm:px-6 lg:hidden">
          <button type="button" className="-m-2.5 p-2.5 text-indigo-200 lg:hidden" onClick={() => setSidebarOpen(true)}>
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
          <div className="flex-1 text-sm font-semibold leading-6 text-white">Dashboard</div>
        </div>

        <main className={`py-5 ${sidebarCollapsed ? 'lg:pl-20' :'lg:pl-60'}`}>
          <div className="px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </>
  )
}
export default Sidebar